import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import api, { apiError } from "@/lib/api";
import { BITACORA } from "@/constants/testIds";
import { BITACORA_CONFIG } from "@/lib/bitacoraConfig";
import { PhotoCapture } from "@/components/PhotoCapture";
import { syncManager } from "@/lib/syncManager";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { feedback } from "@/lib/feedback";
import { useParams } from "react-router-dom";
import { Plus, Trash2, ArrowLeft, Sparkles, FileDown, FileText } from "lucide-react";

export default function BitacoraPage() {
  const { tipo } = useParams();
  const cfg = BITACORA_CONFIG[tipo];
  const { user } = useAuth();
  const qc = useQueryClient();
  const [showForm, setShowForm] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const handleDownloadPDF = async () => {
    try {
      setDownloading(true);

      // Si estamos en un dispositivo móvil (Capacitor)
      if (window.Capacitor?.isNativePlatform()) {
        const token = localStorage.getItem("sisp_token");
        const url = `${api.defaults.baseURL}bitacoras/${tipo}/pdf?token=${token}`;
        window.open(url, '_blank');
        return;
      }

      const res = await api.get(`/bitacoras/${tipo}/pdf`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `bitacora_${tipo}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (e) {
      toast.error("Error al descargar PDF");
    } finally {
      setDownloading(false);
    }
  };
  const [form, setForm] = useState({
    titulo: "",
    ubicacion: "",
    fecha: "",
    datos: {},
    fotos: [],
  });
  const [editId, setEditId] = useState(null);

  const [filterArea, setFilterArea] = useState("all");

  const canDelete = ["admin", "supervisor"].includes(user?.role);

  const { data: items = [], isLoading } = useQuery({
    queryKey: ["bitacora", tipo],
    queryFn: () => api.get(`/bitacoras/${tipo}`).then((r) => r.data),
    enabled: !!cfg,
  });

  const filteredItems = items.filter(it => {
    if (tipo !== "control_cortinas" || filterArea === "all") return true;
    // Búsqueda flexible en el campo area
    const areaVal = String(it.datos?.area || "").toUpperCase();
    const areaFilter = filterArea.toUpperCase();
    return areaVal === areaFilter || areaVal.includes(areaFilter);
  });

  const createMutation = useMutation({
    mutationFn: (body) => editId
      ? api.patch(`/bitacoras/${tipo}/${editId}`, body).then(r => r.data)
      : api.post(`/bitacoras/${tipo}`, body).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bitacora", tipo] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success(editId ? "Registro actualizado" : "Registro creado");
      feedback.success();
      setShowForm(false);
      setEditId(null);
      setForm({ titulo: "", ubicacion: "", fecha: "", datos: {}, fotos: [] });
    },
    onError: (e) => { toast.error(apiError(e, "Error al guardar")); feedback.error(); },
  });

  const handleEdit = (item) => {
    setEditId(item.id);
    setForm({
      titulo: item.titulo || "",
      ubicacion: item.ubicacion || "",
      fecha: item.fecha || "",
      datos: item.datos || {},
      fotos: item.fotos || [],
    });
    setShowForm(true);
  };

  const deleteMutation = useMutation({
    mutationFn: (id) => api.delete(`/bitacoras/${tipo}/${id}`).then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["bitacora", tipo] });
      qc.invalidateQueries({ queryKey: ["dashboard-stats"] });
      toast.success("Registro eliminado");
    },
    onError: (e) => toast.error(apiError(e, "Error al eliminar")),
  });

  const ocrMutation = useMutation({
    mutationFn: (body) => api.post("/ocr/analyze", body).then(r => r.data),
    onSuccess: (data) => {
      // Intentar mapear campos extraídos a los campos de la bitácora
      const mapped = { ...form.datos };
      // Mapeo flexible
      if (data.placas_unidad) mapped.placas = data.placas_unidad;
      if (data.chofer_nombre) mapped.chofer = data.chofer_nombre;
      if (data.compania_transporte) {
        mapped.compania = data.compania_transporte;
        mapped.empresa = data.compania_transporte;
      }
      if (data.sello_seguridad || data.numero_precinto) {
        mapped.sello = data.sello_seguridad || data.numero_precinto;
      }
      if (data.numero_caja || data.numero_trailer) {
        mapped.contenedor = data.numero_caja || data.numero_trailer;
        mapped.unidad = data.numero_caja || data.numero_trailer;
      }

      setForm(prev => ({ ...prev, datos: mapped }));
      toast.success("Información extraída correctamente");
    },
    onError: (e) => toast.error(apiError(e, "Error al analizar")),
  });

  const handleScan = (img) => ocrMutation.mutate({ image_b64: img, context: tipo });

  const submit = (e) => {
    e.preventDefault();
    const required = cfg.fields.filter((f) => f.required);
    for (const f of required) {
      if (!form.datos[f.name]) {
        toast.error(`${f.label} es obligatorio`);
        return;
      }
    }
    const titulo = form.titulo || form.datos[cfg.columns[0]] || cfg.short;
    const payload = { ...form, titulo };

    if (syncManager.isOffline()) {
      const method = editId ? "PATCH" : "POST";
      const url = editId ? `/bitacoras/${tipo}/${editId}` : `/bitacoras/${tipo}`;
      syncManager.addToQueue(url, method, payload, `Bitácora ${cfg.short}`);
      setShowForm(false);
      setEditId(null);
      setForm({ titulo: "", ubicacion: "", fecha: "", datos: {}, fotos: [] });
      return;
    }

    createMutation.mutate(payload);
  };

  const setField = (name, value) => setForm((p) => ({ ...p, datos: { ...p.datos, [name]: value } }));

  if (!cfg) return <div className="p-8 text-muted-foreground">Tipo de bitácora inválido.</div>;

  return (
    <div className="p-4 lg:p-8 max-w-7xl mx-auto">
      <header className="flex items-center justify-between mb-6">
        <div className="flex items-start gap-3">
          <span className={`w-11 h-11 flex items-center justify-center ${
            cfg.accent === "critical" ? "bg-critical/10 text-critical"
            : cfg.accent === "success" ? "bg-success/10 text-success"
            : "bg-navy/5 text-navy"
          }`}>
            <cfg.icon size={22} />
          </span>
          <div>
            <h1 className="font-heading text-3xl lg:text-4xl font-extrabold tracking-tight text-navy">{cfg.label}</h1>
            <p className="text-sm text-muted-foreground mt-1">{cfg.description}</p>
          </div>
        </div>
        <div className="flex gap-2">
          {tipo === "control_cortinas" && (
            <div className="flex bg-white border border-border p-1">
              <button
                onClick={() => setFilterArea("all")}
                className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${filterArea === "all" ? "bg-navy text-white" : "text-navy hover:bg-navy/5"}`}
              >
                Todas
              </button>
              <button
                onClick={() => setFilterArea("EMBARQUE")}
                className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${filterArea === "EMBARQUE" ? "bg-navy text-white" : "text-navy hover:bg-navy/5"}`}
              >
                Embarque
              </button>
              <button
                onClick={() => setFilterArea("RECIBO")}
                className={`px-3 py-1.5 text-[10px] font-bold uppercase tracking-wider transition-colors ${filterArea === "RECIBO" ? "bg-navy text-white" : "text-navy hover:bg-navy/5"}`}
              >
                Recibo
              </button>
            </div>
          )}
          <button
            onClick={handleDownloadPDF}
            disabled={downloading}
            className="flex items-center gap-2 h-11 px-4 border border-navy text-navy font-semibold hover:bg-navy/5 disabled:opacity-50"
          >
            <FileDown size={18} /> {downloading ? "Descargando..." : "Descargar PDF"}
          </button>
          <button
            onClick={() => setShowForm(true)}
            data-testid={BITACORA.newButton}
            className="flex items-center gap-2 h-11 px-4 bg-navy text-white font-semibold hover:bg-navy-hover"
          >
            <Plus size={18} /> Nuevo
          </button>
        </div>
      </header>

      {isLoading ? (
        <p className="text-sm text-muted-foreground">Cargando…</p>
      ) : items.length === 0 ? (
        <div className="bg-white border border-border p-12 flex flex-col items-center text-center">
          <span className="w-14 h-14 bg-secondary text-navy flex items-center justify-center mb-4">
            <cfg.icon size={28} />
          </span>
          <h3 className="font-heading text-xl font-bold text-navy">Sin registros</h3>
          <p className="text-sm text-muted-foreground mt-1 mb-4 max-w-sm">Crea el primer registro de esta bitácora.</p>
          <button
            onClick={() => setShowForm(true)}
            data-testid={BITACORA.newButton}
            className="h-11 px-4 bg-navy text-white font-semibold hover:bg-navy-hover"
          >
            + Nuevo registro
          </button>
        </div>
      ) : (
        <div className="space-y-4">
          {/* Mobile View: Cards */}
          <div className="grid grid-cols-1 gap-4 md:hidden">
            {filteredItems.map((it) => (
              <div key={it.id} className="bg-white border border-border p-4 rounded-lg shadow-sm space-y-3">
                <div className="flex justify-between items-start border-b border-border pb-2">
                  <div>
                    <p className="text-[10px] font-black text-navy/40 uppercase tracking-widest">
                      {it.fecha ? new Date(it.fecha).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "—"}
                    </p>
                    <h4 className="font-bold text-navy text-lg leading-tight">{it.titulo || "Sin título"}</h4>
                  </div>
                  <div className="flex gap-1">
                    <button onClick={() => handleEdit(it)} className="p-2 text-navy hover:bg-navy/10 rounded">
                      <FileText size={18} />
                    </button>
                    {canDelete && (
                      <button
                        onClick={() => { if (confirm("¿Eliminar?")) deleteMutation.mutate(it.id); }}
                        className="p-2 text-critical hover:bg-critical/10 rounded"
                      >
                        <Trash2 size={18} />
                      </button>
                    )}
                  </div>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-3 gap-x-4">
                  {cfg.columns.map((c) => {
                    const f = cfg.fields.find((x) => x.name === c);
                    return (
                      <div key={c} className="border-l-2 border-navy/10 pl-2">
                        <p className="text-[9px] font-black text-navy/40 uppercase tracking-tighter leading-none">{f?.label || c}</p>
                        <p className="text-[11px] font-bold text-navy mt-0.5 break-all">{it.datos?.[c] || "—"}</p>
                      </div>
                    );
                  })}
                </div>
                {it.ubicacion && (
                  <div className="pt-2 border-t border-border flex items-center gap-2">
                    <p className="text-[10px] text-muted-foreground uppercase font-medium">Ubicación: {it.ubicacion}</p>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Desktop View: Table */}
          <div className="hidden md:block bg-white border border-border overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-secondary border-b border-border">
                  <tr className="text-left text-xs uppercase tracking-wide text-muted-foreground">
                    {cfg.columns.map((c) => {
                      const f = cfg.fields.find((x) => x.name === c);
                      return <th key={c} className="px-4 py-3 font-medium">{f?.label || c}</th>;
                    })}
                    <th className="px-4 py-3 font-medium hidden md:table-cell">Ubicación</th>
                    <th className="px-4 py-3 font-medium hidden md:table-cell">Fecha</th>
                    <th className="px-4 py-3 font-medium hidden lg:table-cell">Por</th>
                    {canDelete && <th className="px-4 py-3"></th>}
                  </tr>
                </thead>
                <tbody data-testid={BITACORA.list} className="divide-y divide-border">
                  {filteredItems.map((it) => (
                    <tr key={it.id} data-testid={BITACORA.row} className="hover:bg-secondary/50">
                      {cfg.columns.map((c) => (
                        <td key={c} className="px-4 py-3 text-navy">{it.datos?.[c] || "—"}</td>
                      ))}
                      <td className="px-4 py-3 hidden md:table-cell text-muted-foreground">{it.ubicacion || "—"}</td>
                      <td className="px-4 py-3 hidden md:table-cell text-muted-foreground text-xs">
                        {it.fecha ? new Date(it.fecha).toLocaleString("es-MX", { dateStyle: "short", timeStyle: "short" }) : "—"}
                      </td>
                      <td className="px-4 py-3 hidden md:table-cell text-muted-foreground text-xs">{it.created_by}</td>
                      <td className="px-4 py-3 text-right flex gap-1 justify-end">
                        <button
                          onClick={() => handleEdit(it)}
                          className="inline-flex items-center justify-center w-8 h-8 text-navy hover:bg-navy/10 rounded"
                          aria-label="Editar"
                        >
                          <FileText size={16} />
                        </button>
                        {canDelete && (
                          <button
                            onClick={() => {
                              if (confirm("¿Eliminar este registro de bitácora?")) deleteMutation.mutate(it.id);
                            }}
                            data-testid={BITACORA.deleteButton}
                            className="inline-flex items-center justify-center w-8 h-8 text-critical hover:bg-critical/10 rounded"
                            aria-label="Eliminar"
                          >
                            <Trash2 size={16} />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/60">
          <div className="bg-white border border-border w-full max-w-2xl max-h-[90vh] overflow-y-auto p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-heading text-xl font-bold text-navy">
                {editId ? "Modificar registro" : "Nuevo registro"} · {cfg.short}
              </h3>
              <button
                onClick={() => {
                  setShowForm(false);
                  setEditId(null);
                  setForm({ titulo: "", ubicacion: "", fecha: "", datos: {}, fotos: [] });
                }}
                className="p-2 hover:bg-secondary"
                aria-label="Cerrar"
              >
                <ArrowLeft size={18} />
              </button>
            </div>
            <form onSubmit={submit} className="space-y-4">
              <div className="p-4 bg-navy/5 border border-navy/10 space-y-3">
                <div className="flex items-center gap-2">
                  <Sparkles size={16} className="text-navy" />
                  <span className="text-xs font-bold text-navy uppercase">Autocompletar con IA</span>
                </div>
                <PhotoCapture
                  single
                  label="Cargar foto de documento o evidencia"
                  onChange={(v) => handleScan(v[0])}
                  testid="bitacora-ia-scan"
                />
              </div>
              <div className="grid md:grid-cols-2 gap-4">
                {cfg.fields.map((f) => (
                  <Field key={f.name} label={f.label} required={f.required} full={f.type === "textarea"}>
                    {f.type === "textarea" ? (
                      <textarea
                        value={form.datos[f.name] || ""}
                        onChange={(e) => setField(f.name, e.target.value)}
                        className="input min-h-[70px]"
                      />
                    ) : f.type === "time" ? (
                      <input
                        type="time"
                        value={form.datos[f.name] || ""}
                        onChange={(e) => setField(f.name, e.target.value)}
                        className="input"
                      />
                    ) : (
                      <input
                        type={f.type === "number" ? "number" : "text"}
                        value={form.datos[f.name] || ""}
                        onChange={(e) => setField(f.name, e.target.value)}
                        className="input"
                      />
                    )}
                  </Field>
                ))}
              </div>
              <Field label="Ubicación">
                <input
                  value={form.ubicacion}
                  onChange={(e) => setForm({ ...form, ubicacion: e.target.value })}
                  className="input"
                />
              </Field>
              <Field label="Fecha y hora">
                <input
                  type="datetime-local"
                  value={form.fecha ? form.fecha.slice(0, 16) : ""}
                  onChange={(e) => setForm({ ...form, fecha: e.target.value ? new Date(e.target.value).toISOString() : "" })}
                  className="input"
                />
              </Field>
              <PhotoCapture
                label="Fotografías"
                value={form.fotos}
                onChange={(v) => setForm({ ...form, fotos: v })}
                testid="bitacora-foto"
              />
              <div className="flex gap-3 pt-2">
                <button
                  type="submit"
                  data-testid={BITACORA.formSubmit}
                  disabled={createMutation.isPending}
                  className="h-11 px-5 bg-navy text-white font-semibold hover:bg-navy-hover disabled:opacity-50"
                >
                  {createMutation.isPending ? "Guardando…" : "Guardar"}
                </button>
                <button
                  type="button"
                  onClick={() => setShowForm(false)}
                  data-testid={BITACORA.formCancel}
                  className="h-11 px-5 border border-border text-navy hover:bg-secondary"
                >
                  Cancelar
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

function Field({ label, children, required, full }) {
  return (
    <div className={`space-y-1.5 ${full ? "md:col-span-2" : ""}`}>
      <label className="text-sm font-medium text-navy">
        {label}{required && <span className="text-critical"> *</span>}
      </label>
      {children}
    </div>
  );
}
